import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Reveal from '../components/Reveal';
import { apiFetch } from '../lib/api';
import './ProductCollection.css';

interface Product {
  id: number;
  name: string;
  category: string;
  price: number;
  description: string;
  image: string;
  status: string;
  featured: boolean;
}

interface Category {
  id: string;
  name: string;
  description: string;
}

type AvailabilityFilter = 'all' | 'available' | 'coming-soon';

type SortKey = 'featured' | 'price-low' | 'price-high' | 'name-asc' | 'name-desc';

const ProductCollection: React.FC = () => {
  const { category } = useParams<{ category: string }>();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<SortKey>('featured');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [availability, setAvailability] = useState<AvailabilityFilter>('all');
  const [priceMin, setPriceMin] = useState<string>('');
  const [priceMax, setPriceMax] = useState<string>('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [visibleCount, setVisibleCount] = useState<number>(12);

  const pageSize = 12;
  const fallbackProductImage = '/images/unnamed.jpg';

  const handleCardHover = useCallback((el: HTMLElement | null) => {
    if (!el) return;
    if (el.dataset.autoScroll === '1') return;

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const run = () => {
      const rect = el.getBoundingClientRect();
      const safety = 24;
      const overflow = rect.bottom - (window.innerHeight - safety);
      if (overflow > 0) {
        window.scrollBy({
          top: Math.min(overflow + 8, 220),
          left: 0,
          behavior: prefersReducedMotion ? 'auto' : 'smooth'
        });
      }
      el.dataset.autoScroll = '1';
      window.setTimeout(() => {
        if (el.isConnected) el.dataset.autoScroll = '0';
      }, 650);
    };

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(run);
    });
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('Fetching products for category:', category);
        const [productsResponse, categoriesResponse] = await Promise.all([
          apiFetch<{products: Product[]}>(`/api/products${category ? `?category=${category}` : ''}`),
          apiFetch<{categories: string[]}>('/api/products/meta/categories')
        ]);

        console.log('Products response:', productsResponse);
        console.log('Categories response:', categoriesResponse);
        
        setProducts(productsResponse.products || []);
        // Convert category strings to Category objects
        const categoryObjects = (categoriesResponse.categories || []).map(cat => ({
          id: cat,
          name: cat.charAt(0).toUpperCase() + cat.slice(1),
          description: `Explore our ${cat} collection`
        }));
        setCategories(categoryObjects);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [category]);

  useEffect(() => {
    setVisibleCount(pageSize);
  }, [category]);

  const getCurrentCategory = () => {
    return categories.find(cat => cat.id === category);
  };

  const availableTags = useMemo(() => {
    const set = new Set<string>();
    for (const p of products) {
      if (p.featured) set.add('Featured');
      if (p.status === 'available') set.add('Available');
      if (p.status === 'coming-soon') set.add('Coming soon');
      if (p.category) set.add(p.category);
    }
    return Array.from(set);
  }, [products]);

  const filteredProducts = useMemo(() => {
    const min = priceMin.trim() ? Number(priceMin) : undefined;
    const max = priceMax.trim() ? Number(priceMax) : undefined;

    return products.filter((p) => {
      if (availability === 'available' && p.status !== 'available') return false;
      if (availability === 'coming-soon' && p.status !== 'coming-soon') return false;

      if (typeof min === 'number' && !Number.isNaN(min) && Number(p.price) < min) return false;
      if (typeof max === 'number' && !Number.isNaN(max) && Number(p.price) > max) return false;

      if (selectedTags.length > 0) {
        const tags: string[] = [];
        if (p.featured) tags.push('Featured');
        if (p.status === 'available') tags.push('Available');
        if (p.status === 'coming-soon') tags.push('Coming soon');
        if (p.category) tags.push(p.category);

        const hasAny = selectedTags.some((t) => tags.includes(t));
        if (!hasAny) return false;
      }

      return true;
    });
  }, [availability, priceMax, priceMin, products, selectedTags]);

  const sortedProducts = useMemo(() => {
    switch (sortBy) {
      case 'price-low':
        return [...filteredProducts].sort((a, b) => Number(a.price) - Number(b.price));
      case 'price-high':
        return [...filteredProducts].sort((a, b) => Number(b.price) - Number(a.price));
      case 'name-asc':
        return [...filteredProducts].sort((a, b) => a.name.localeCompare(b.name));
      case 'name-desc':
        return [...filteredProducts].sort((a, b) => b.name.localeCompare(a.name));
      case 'featured':
      default:
        return [...filteredProducts].sort((a, b) => {
          const af = a.featured ? 0 : 1;
          const bf = b.featured ? 0 : 1;
          if (af !== bf) return af - bf;
          return a.name.localeCompare(b.name);
        });
    }
  }, [filteredProducts, sortBy]);

  const visibleProducts = useMemo(() => sortedProducts.slice(0, visibleCount), [sortedProducts, visibleCount]);

  useEffect(() => {
    setVisibleCount(pageSize);
  }, [availability, priceMin, priceMax, selectedTags, sortBy]);

  useEffect(() => {
    if (!isFilterOpen) return;

    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFilterOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isFilterOpen]);

  const currentCategory = getCurrentCategory();

  const headerBg =
    category === 'productivity'
      ? '/images/burst/hero-organized-workspace.jpg'
      : category === 'mobility'
        ? '/images/burst/hero-working-from-home.jpg'
        : '/images/burst/hero-minimal-workspace.jpg';

  useEffect(() => {
    const pageTitle = currentCategory?.name ? `${currentCategory.name} – ARVIX` : 'Collections – ARVIX';
    document.title = pageTitle;

    const description = currentCategory?.description || '';
    if (!description) return;

    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute('content', description);
    }
  }, [currentCategory?.name, currentCategory?.description]);

  const isProductivity = category === 'productivity';
  const productivityCopy =
    'A collection of useful tools that transform tedious daily tasks to enjoyable activities. Not only does the collection boost efficiency but also adds a touch of fun to everyday chores.';

  const storyCards = useMemo(() => {
    if (!isProductivity) return [] as { title: string; body: string; cta: { label: string; to?: string; disabled?: boolean }; image: string }[];

    return [
      {
        title: 'Memo X',
        body: 'Complete cutting and applying tapes in one motion. Make creative sticky memos with papers of your choice in any sizes.',
        cta: { label: 'Shop Now', to: '/collections/productivity' },
        image: '/images/burst/product-flatlay-notebooks.jpg'
      },
      {
        title: 'Tool Card',
        body:
          'The Tool Card is more than a tool card you commonly find in the market. Equipped with at least 14 functions, the Tool Card is the forefront of innovation that it allows for the storage of your iPhone ejector tool with a simple push & click.',
        cta: { label: 'Coming soon', disabled: true },
        image: '/images/burst-products/p1-wireless-headphones.jpg'
      },
      {
        title: 'CoinSlide',
        body: 'Make collecting coins fast and fun! The first ever palm-sized auto-sorting coin device perfect for paying and storing.',
        cta: { label: 'Coming soon', disabled: true },
        image: '/images/burst-products/p2-black-earbuds.jpg'
      }
    ];
  }, [isProductivity]);

  if (loading) {
    return (
      <div className={`product-collection ${isProductivity ? 'product-collection--productivity' : ''}`.trim()}>
        <div className="container">
          <div
            className="collection-header collection-header--with-bg"
            style={{ backgroundImage: `url('${headerBg}')` }}
          >
            <div className="collection-header__overlay" />
            <div className="collection-header__content">
              <div className="skeleton skeleton-pill" style={{ width: 240 }} />
              <div className="skeleton skeleton-title" style={{ width: 320, margin: '18px auto 0' }} />
              <div className="skeleton skeleton-text" style={{ width: 520, margin: '14px auto 0' }} />
            </div>
          </div>

          <div className="collection-controls">
            <div className="skeleton skeleton-text" style={{ width: 120 }} />
            <div className="select-wrap select-wrap--skeleton">
              <div className="skeleton skeleton-pill" style={{ width: 190 }} />
            </div>
          </div>

          <div className="products-grid">
            {Array.from({ length: 6 }).map((_, idx) => (
              <div key={`s-${idx}`} className="product-card product-card--link" aria-hidden="true">
                <div className="product-image">
                  <div className="skeleton skeleton-media" />
                </div>
                <div className="product-info">
                  <div className="skeleton skeleton-title" style={{ width: '68%' }} />
                  <div className="skeleton skeleton-text" style={{ width: '92%', marginTop: 10 }} />
                  <div className="skeleton skeleton-text" style={{ width: '78%', marginTop: 10 }} />
                  <div className="product-footer" style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div className="skeleton skeleton-pill" style={{ width: 72, height: 18 }} />
                    <div className="skeleton skeleton-pill" style={{ width: 120, height: 34 }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`product-collection ${isProductivity ? 'product-collection--productivity' : ''}`.trim()}>
      <div className="container">
        {/* Header */}
        <Reveal>
          {isProductivity ? (
            <div className="collection-header collection-header--plain">
              <div className="collection-header__content">
                <h1>Productivity</h1>
                <p className="collection-description">{productivityCopy}</p>
              </div>
            </div>
          ) : (
            <div
              className="collection-header collection-header--with-bg"
              style={{ backgroundImage: `url('${headerBg}')` }}
            >
              <div className="collection-header__overlay" />
              <div className="collection-header__content">
                <nav className="breadcrumb">
                  <Link to="/">Home</Link>
                  <span>/</span>
                  <span>{currentCategory?.name || 'All Products'}</span>
                </nav>

                <h1>{currentCategory?.name || 'All Products'}</h1>
                {currentCategory && (
                  <p className="collection-description">{currentCategory.description}</p>
                )}
              </div>
            </div>
          )}
        </Reveal>

        {isProductivity && (
          <div className="collection-story">
            {storyCards.map((card, idx) => (
              <Reveal key={card.title} delayMs={80 * idx}>
                <div className="collection-story__item">
                  <div className="collection-story__visual">
                    <div className="collection-story__image-wrapper">
                      <img
                        className="collection-story__image"
                        src={card.image}
                        alt={card.title}
                        loading="lazy"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = fallbackProductImage;
                        }}
                      />
                    </div>
                  </div>

                  <div className="collection-story__content">
                    <h2 className="collection-story__title">{card.title}</h2>
                    <p className="collection-story__copy">{card.body}</p>
                    <div className="collection-story__cta">
                      {card.cta.disabled ? (
                        <span className="collection-story__soon">{card.cta.label}</span>
                      ) : (
                        <Link className="collection-story__link collection-story__link--primary" to={card.cta.to || '/'}>
                          {card.cta.label}
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}

        {/* Filters and Sort */}
        <Reveal delayMs={80}>
          <div className="collection-controls">
            <div className="results-count">
              {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
            </div>

            <div className="collection-filters">
              <button
                type="button"
                className="filter-btn"
                onClick={() => setIsFilterOpen(true)}
              >
                Filter
              </button>

              <div className="sort-controls">
                <label htmlFor="sort">Sort by:</label>
                <div className="select-wrap">
                  <select
                    id="sort"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as SortKey)}
                  >
                    <option value="featured">Featured</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="name-asc">Name: A-Z</option>
                    <option value="name-desc">Name: Z-A</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        <div className={`filter-drawer ${isFilterOpen ? 'is-open' : ''}`.trim()} aria-hidden={!isFilterOpen}>
          <div className="filter-drawer__backdrop" onClick={() => setIsFilterOpen(false)} />
          <div className="filter-drawer__panel" role="dialog" aria-modal="true" aria-label="Filter products">
            <div className="filter-drawer__header">
              <div className="filter-drawer__title">Filter</div>
              <button type="button" className="filter-drawer__close" onClick={() => setIsFilterOpen(false)} aria-label="Close filters">
                ✕
              </button>
            </div>

            <div className="filter-drawer__body">
              <div className="filter-group">
                <div className="filter-group__label">Availability</div>
                <label className="filter-radio">
                  <input type="radio" name="availability" checked={availability === 'all'} onChange={() => setAvailability('all')} />
                  <span>All</span>
                </label>
                <label className="filter-radio">
                  <input type="radio" name="availability" checked={availability === 'available'} onChange={() => setAvailability('available')} />
                  <span>Available</span>
                </label>
                <label className="filter-radio">
                  <input type="radio" name="availability" checked={availability === 'coming-soon'} onChange={() => setAvailability('coming-soon')} />
                  <span>Coming soon</span>
                </label>
              </div>

              <div className="filter-group">
                <div className="filter-group__label">Price range</div>
                <div className="filter-price">
                  <div className="filter-price__row">
                    <span className="filter-price__prefix">$</span>
                    <input value={priceMin} onChange={(e) => setPriceMin(e.target.value)} inputMode="decimal" placeholder="Min" />
                  </div>
                  <div className="filter-price__row">
                    <span className="filter-price__prefix">$</span>
                    <input value={priceMax} onChange={(e) => setPriceMax(e.target.value)} inputMode="decimal" placeholder="Max" />
                  </div>
                </div>
              </div>

              <div className="filter-group">
                <div className="filter-group__label">Category tag</div>
                <div className="filter-tags">
                  {availableTags.map((t) => (
                    <label key={t} className="filter-check">
                      <input
                        type="checkbox"
                        checked={selectedTags.includes(t)}
                        onChange={(e) => {
                          setSelectedTags((prev) => {
                            if (e.target.checked) return prev.includes(t) ? prev : [...prev, t];
                            return prev.filter((x) => x !== t);
                          });
                        }}
                      />
                      <span>{t}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="filter-drawer__footer">
              <button
                type="button"
                className="filter-clear"
                onClick={() => {
                  setAvailability('all');
                  setPriceMin('');
                  setPriceMax('');
                  setSelectedTags([]);
                }}
              >
                Clear
              </button>
              <button type="button" className="filter-apply" onClick={() => setIsFilterOpen(false)}>
                View results
              </button>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        {sortedProducts.length > 0 ? (
          <div className="products-grid">
            {visibleProducts.map((product, idx) => (
              <Reveal key={product.id} delayMs={60 * idx}>
                {product.status === 'available' ? (
                  <Link
                    to={`/product/${product.id}`}
                    className="product-card product-card--link"
                    onMouseEnter={(e) => handleCardHover(e.currentTarget)}
                    aria-label={`View ${product.name}`}
                  >
                    <div className="product-image">
                      <img 
                        src={product.image} 
                        alt={product.name}
                        loading="lazy"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = fallbackProductImage;
                        }}
                      />
                      {product.featured && <div className="featured-badge">Featured</div>}
                    </div>
                    <div className="product-info">
                      <h3>{product.name}</h3>
                      <div className="product-expand">
                        <p className="product-description">{product.description}</p>
                      </div>
                      <div className="product-footer">
                        <span className="price">${product.price}</span>
                        <span className="btn-view-details">View Details</span>
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div
                    className="product-card is-disabled"
                    aria-disabled="true"
                    aria-label={`${product.name} coming soon`}
                  >
                    <div className="product-image">
                      <img 
                        src={product.image} 
                        alt={product.name}
                        loading="lazy"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = fallbackProductImage;
                        }}
                      />
                      <div className="coming-soon-badge">Coming Soon</div>
                      {product.featured && <div className="featured-badge">Featured</div>}
                    </div>
                    <div className="product-info">
                      <h3>{product.name}</h3>
                      <div className="product-expand">
                        <p className="product-description">{product.description}</p>
                      </div>
                      <div className="product-footer">
                        <span className="price">${product.price}</span>
                        <span className="coming-soon-text">Coming Soon</span>
                      </div>
                    </div>
                  </div>
                )}
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="no-products">
            <h2>No products found</h2>
            <p>Check back later for new arrivals in this category.</p>
          </div>
        )}

        {sortedProducts.length > 0 && visibleProducts.length < sortedProducts.length && (
          <div className="collection-loadmore">
            <button
              type="button"
              className="loadmore-btn"
              onClick={() => setVisibleCount((v) => Math.min(v + pageSize, sortedProducts.length))}
            >
              Load more
            </button>
          </div>
        )}

        {/* Category Navigation */}
        <Reveal>
          <div className="category-nav">
            <h3>Shop Other Categories</h3>
            <div className="category-links">
              {categories
                .filter((cat) => cat.id !== category)
                .map((cat) => (
                  <Link key={cat.id} to={`/collections/${cat.id}`} className="category-link">
                    {cat.name}
                  </Link>
                ))}
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
};

export default ProductCollection;
